/**
 * useClientChangeOrders — client-safe change orders for the current project.
 * Excludes internal/draft COs, builder cost, internal notes, and non-client attachments.
 */
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { createNotification } from '@/lib/notifications';
import {
  isClientChangeOrderRecordVisible,
  isClientFacingChangeOrderAttachment,
  isChangeOrderRowArchived,
} from '@/lib/changeOrderUtils';

export interface ClientChangeOrderFile {
  id: string;
  file_url: string;
  file_name: string;
  file_type: string | null;
  storage_bucket?: string | null;
  storage_path?: string | null;
}

export interface ClientChangeOrder {
  id: string;
  co_number: string | null;
  title: string;
  client_description: string | null;
  reason: string | null;
  status: string;
  requires_client_approval: boolean;
  client_decision: string | null;
  client_comment: string | null;
  client_decided_at: string | null;
  client_price: number | null;
  schedule_impact: string | null;
  current_finish_description: string | null;
  new_finish_description: string | null;
  created_at: string;
  updated_at: string | null;
  files: ClientChangeOrderFile[];
}

export interface UseClientChangeOrdersResult {
  changeOrders: ClientChangeOrder[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  submitResponse: (id: string, action: 'approved' | 'declined', comment: string) => Promise<boolean>;
  pendingCount: number;
}

function isPendingStatus(status: string): boolean {
  const s = status.toLowerCase();
  return (
    s === 'pending' ||
    s === 'sent' ||
    s === 'sent_to_client' ||
    s === 'pending_client_approval' ||
    s === 'pending_approval' ||
    s === 'in_progress'
  );
}

function mapChangeOrderStatus(clientAction: 'approved' | 'declined'): string {
  return clientAction === 'approved' ? 'client_approved' : 'client_rejected';
}

export function useClientChangeOrders(projectId: string | null): UseClientChangeOrdersResult {
  const [changeOrders, setChangeOrders] = useState<ClientChangeOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    if (!projectId) {
      setChangeOrders([]);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);

    const { data: coData, error: coErr } = await supabase
      .from('jphph_change_orders')
      .select(
        'id, project_id, title, client_description, reason, status, requires_client_approval, client_decision, client_comment, client_decided_at, co_number, co_sequence, client_price, schedule_impact, current_finish_description, new_finish_description, client_visible, created_at, updated_at'
      )
      .eq('project_id', projectId)
      .order('co_sequence', { ascending: true, nullsFirst: false });

    if (coErr) {
      console.error('[useClientChangeOrders] fetch error:', {
        projectId,
        message: coErr.message,
        details: coErr.details,
        hint: coErr.hint,
        code: coErr.code,
      });
      setChangeOrders([]);
      setError(coErr.message);
      setLoading(false);
      return;
    }

    const rawRows = (coData ?? []) as Record<string, unknown>[];
    const activeRows = rawRows.filter((co) => !isChangeOrderRowArchived({
      status: String(co.status || ''),
      is_deleted: co.is_deleted as boolean | null | undefined,
      archived_at: co.archived_at as string | null | undefined,
    }));

    const rows = activeRows.filter((co) =>
      isClientChangeOrderRecordVisible({
        status: String(co.status || ''),
        client_visible: co.client_visible as boolean | null,
        requires_client_approval: co.requires_client_approval as boolean | null,
      })
    );

    if (import.meta.env.DEV) {
      console.log('[useClientChangeOrders] loaded', {
        projectId,
        rawCount: rawRows.length,
        activeCount: activeRows.length,
        visibleCount: rows.length,
      });
    }

    const uniqueRows = Array.from(new Map(rows.map((co) => [co.id, co])).values());
    let filesMap: Record<string, ClientChangeOrderFile[]> = {};

    if (uniqueRows.length > 0) {
      const coIds = uniqueRows.map((co) => co.id as string);
      const { data: filesData, error: filesErr } = await supabase
        .from('jphph_project_files')
        .select('id, file_name, file_url, file_type, photo_type, change_order_id, is_client_visible, storage_bucket, storage_path')
        .in('change_order_id', coIds)
        .eq('is_deleted', false);

      if (filesErr) {
        console.error('[useClientChangeOrders] files fetch error:', {
          projectId,
          message: filesErr.message,
        });
      }

      (filesData ?? []).forEach((f: Record<string, unknown>) => {
        if (!isClientFacingChangeOrderAttachment({
          photo_type: f.photo_type as string | null,
          is_client_visible: f.is_client_visible as boolean | null,
        })) {
          return;
        }
        const coId = f.change_order_id as string;
        if (!filesMap[coId]) filesMap[coId] = [];
        filesMap[coId].push({
          id: f.id as string,
          file_url: f.file_url as string,
          file_name: f.file_name as string,
          file_type: f.file_type as string | null,
          storage_bucket: f.storage_bucket as string | null ?? null,
          storage_path: f.storage_path as string | null ?? null,
        });
      });
    }

    const mapped: ClientChangeOrder[] = uniqueRows.map((co) => ({
      id: co.id as string,
      co_number: co.co_number as string | null,
      title: co.title as string,
      client_description: co.client_description as string | null,
      reason: co.reason as string | null,
      status: co.status as string,
      requires_client_approval: (co.requires_client_approval as boolean | null) ?? false,
      client_decision: co.client_decision as string | null,
      client_comment: co.client_comment as string | null,
      client_decided_at: co.client_decided_at as string | null,
      client_price: co.client_price as number | null,
      schedule_impact: co.schedule_impact as string | null,
      current_finish_description: co.current_finish_description as string | null,
      new_finish_description: co.new_finish_description as string | null,
      created_at: co.created_at as string,
      updated_at: (co.updated_at as string | null) ?? null,
      files: filesMap[co.id as string] ?? [],
    }));

    setChangeOrders(mapped);
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Cross-device parity with KIWAMI: cancellations and approvals refresh without reload.
  useEffect(() => {
    if (!projectId) return;
    const channel = supabase
      .channel(`jph-client-change-orders-${projectId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'jphph_change_orders', filter: `project_id=eq.${projectId}` }, () => { void fetchAll(); })
      .subscribe();
    return () => { void supabase.removeChannel(channel); };
  }, [fetchAll, projectId]);

  const submitResponse = useCallback(
    async (id: string, action: 'approved' | 'declined', comment: string): Promise<boolean> => {
      if (!projectId) return false;
      const co = changeOrders.find((c) => c.id === id);
      if (!co) return false;

      const now = new Date().toISOString();
      const { error: coErr } = await supabase
        .from('jphph_change_orders')
        .update({
          status: mapChangeOrderStatus(action),
          client_decision: action,
          client_comment: comment.trim() || null,
          client_decided_at: now,
          updated_at: now,
        })
        .eq('id', id);

      if (coErr) {
        console.error('[useClientChangeOrders] update error:', coErr);
        return false;
      }

      await createNotification({
        project_id: projectId,
        notification_type: 'instruction_reply',
        title: action === 'approved' ? `Client approved: ${co.title}` : `Client declined: ${co.title}`,
        body: comment.trim() || `Client ${action} the change order`,
        target_role: 'management',
        link_path: `/management/projects/${projectId}?tab=Change Orders`,
        target_type: 'change_orders',
        target_id: id,
        related_table: 'jphph_change_orders',
        related_id: id,
      });

      await fetchAll();
      return true;
    },
    [changeOrders, projectId, fetchAll]
  );

  const pendingCount = changeOrders.filter(
    (co) => co.requires_client_approval && isPendingStatus(co.status)
  ).length;

  return {
    changeOrders,
    loading,
    error,
    refresh: fetchAll,
    submitResponse,
    pendingCount,
  };
}
